import styled from '@emotion/styled';
import { ReactNode } from 'react';
import ErrorState from '@comp/ErrorState/ErrorState';
import { mediaQuery } from '@styles/index';
import UpscCurrentAffairsSkeleton from './UpscCurrentAffairsSkeleton';

const PageWrap = styled.div<{ $centered?: boolean }>`
  max-width: 1136px;
  margin: 0 auto;
  padding: ${({ $centered }) => ($centered ? '48px 16px' : '18px 16px 40px')};
`;

const CenteredState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 16px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border: 2px solid var(--color-divider);
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  cursor: pointer;
  background: var(--color-base-1);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--color-accent-blue);
    color: var(--color-accent-blue);
    background: var(--color-i-blue-11);
  }
`;

const SubjectWiseGrid = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 16px;

  ${mediaQuery.uptoTablet} {
    grid-template-columns: 1fr;
  }
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 280px;
  gap: 16px;

  ${mediaQuery.uptoTablet} {
    grid-template-columns: 1fr;
  }
`;

const ArticleCardSkeleton = styled.div`
  padding: 14px 16px;
  border: 1px solid var(--color-divider);
  border-radius: 10px;
  background: var(--color-base-1);
`;

const DetailCardSkeleton = styled.div`
  padding: 24px;
  border: 1px solid var(--color-divider);
  border-radius: 10px;
  background: var(--color-base-1);
`;

const SidebarSkeleton = styled.div`
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--color-divider);
  border-radius: 10px;
  background: var(--color-base-1);
`;

const ArticleListSkeleton = ({ count = 4 }: { count?: number }) => (
  <div aria-busy="true" aria-label="Loading articles">
    {Array.from({ length: count }, (_, index) => (
      <ArticleCardSkeleton key={index} style={{ marginBottom: 10 }}>
        <UpscCurrentAffairsSkeleton height="12px" width="120px" />
        <UpscCurrentAffairsSkeleton
          height="18px"
          width="85%"
          style={{ marginTop: 10 }}
        />
        <UpscCurrentAffairsSkeleton
          height="14px"
          width="100%"
          style={{ marginTop: 10 }}
        />
        <UpscCurrentAffairsSkeleton
          height="14px"
          width="72%"
          style={{ marginTop: 6 }}
        />
      </ArticleCardSkeleton>
    ))}
  </div>
);

export type UpscCurrentAffairsLoadingVariant =
  | 'article-list'
  | 'subject-wise'
  | 'analysis-grid'
  | 'detail';

export interface UpscCurrentAffairsLoadingStateProps {
  variant?: UpscCurrentAffairsLoadingVariant;
}

export const UpscCurrentAffairsLoadingState = ({
  variant = 'article-list'
}: UpscCurrentAffairsLoadingStateProps) => {
  if (variant === 'detail') {
    return (
      <PageWrap $centered aria-busy="true" aria-label="Loading article">
        <DetailCardSkeleton>
          <UpscCurrentAffairsSkeleton height="14px" width="160px" />
          <UpscCurrentAffairsSkeleton
            height="28px"
            width="90%"
            style={{ marginTop: 14 }}
          />
          <UpscCurrentAffairsSkeleton
            height="16px"
            width="100%"
            style={{ marginTop: 16 }}
          />
          <UpscCurrentAffairsSkeleton
            height="16px"
            width="96%"
            style={{ marginTop: 8 }}
          />
          <UpscCurrentAffairsSkeleton
            height="16px"
            width="88%"
            style={{ marginTop: 8 }}
          />
          <UpscCurrentAffairsSkeleton
            height="120px"
            width="100%"
            style={{ marginTop: 20 }}
          />
        </DetailCardSkeleton>
      </PageWrap>
    );
  }

  if (variant === 'analysis-grid') {
    return (
      <PageWrap aria-busy="true" aria-label="Loading today's analysis">
        <AnalysisGrid>
          <SidebarSkeleton>
            <UpscCurrentAffairsSkeleton height="14px" width="70%" />
            {Array.from({ length: 5 }, (_, index) => (
              <UpscCurrentAffairsSkeleton
                key={index}
                height="32px"
                width="100%"
              />
            ))}
          </SidebarSkeleton>
          <ArticleListSkeleton count={3} />
          <SidebarSkeleton>
            <UpscCurrentAffairsSkeleton height="14px" width="60%" />
            <UpscCurrentAffairsSkeleton height="80px" width="100%" />
            <UpscCurrentAffairsSkeleton height="80px" width="100%" />
          </SidebarSkeleton>
        </AnalysisGrid>
      </PageWrap>
    );
  }

  if (variant === 'subject-wise') {
    return (
      <PageWrap aria-busy="true" aria-label="Loading subject-wise page">
        <UpscCurrentAffairsSkeleton
          height="38px"
          width="100%"
          style={{ marginBottom: 16 }}
        />
        <SubjectWiseGrid>
          <SidebarSkeleton>
            <UpscCurrentAffairsSkeleton height="14px" width="80%" />
            {Array.from({ length: 6 }, (_, index) => (
              <UpscCurrentAffairsSkeleton
                key={index}
                height="36px"
                width="100%"
              />
            ))}
          </SidebarSkeleton>
          <ArticleListSkeleton />
        </SubjectWiseGrid>
      </PageWrap>
    );
  }

  return <ArticleListSkeleton />;
};

export interface UpscCurrentAffairsErrorStateProps {
  title?: string;
  subtitle?: string;
  onRetry?: () => void;
  compact?: boolean;
  children?: ReactNode;
}

export const UpscCurrentAffairsErrorState = ({
  title = 'Something went wrong',
  subtitle = 'There was an issue loading current affairs content. Please try again.',
  onRetry,
  compact = false,
  children
}: UpscCurrentAffairsErrorStateProps) => {
  const content = (
    <CenteredState>
      <ErrorState title={title} subtitle={subtitle} />
      {onRetry ? (
        <RetryButton type="button" onClick={onRetry}>
          Try again
        </RetryButton>
      ) : null}
      {children}
    </CenteredState>
  );

  if (compact) {
    return <div role="alert">{content}</div>;
  }

  return <PageWrap $centered>{content}</PageWrap>;
};
